import { create } from 'zustand';
import type { PracticeNavigationTreeSection } from '../utils/navigationTree';

type PracticeQuestionSelectHandler = (questionId: string) => void;

export type PracticeQuestionFilter = {
  frequencyScope: 'all' | 'high_frequency' | 'regular';
  newOnly: boolean;
};

export type PracticeQuestionFilterChangeHandler = (next: PracticeQuestionFilter) => void;

type PracticeShellState = {
  navigationVisible: boolean;
  sidebarLabel: string;
  drawerLabel: string;
  currentQuestionId: string;
  currentIndex: number;
  totalCount: number;
  sidebarSections: PracticeNavigationTreeSection[];
  drawerSections: PracticeNavigationTreeSection[];
  onSelectQuestion: PracticeQuestionSelectHandler | null;
  onLoadMore: (() => void) | null;
  navigationLoadingMore: boolean;
  navigationFilterEnabled: boolean;
  navigationFilter: PracticeQuestionFilter;
  navigationFilterShowFrequency: boolean;
  navigationFilterShowNew: boolean;
  onNavigationFilterChange: PracticeQuestionFilterChangeHandler | null;
  drawerOpen: boolean;
  navigationExpansion: 'question' | 'type' | 'section';
  setNavigation: (payload: {
    visible: boolean;
    sidebarLabel: string;
    drawerLabel: string;
    currentQuestionId: string;
    currentIndex: number;
    totalCount: number;
    sidebarSections: PracticeNavigationTreeSection[];
    drawerSections: PracticeNavigationTreeSection[];
    onSelectQuestion: PracticeQuestionSelectHandler;
    onLoadMore?: () => void;
    navigationLoadingMore?: boolean;
    navigationFilter?: {
      enabled?: boolean;
      value?: PracticeQuestionFilter;
      showFrequency?: boolean;
      showNew?: boolean;
      onChange?: PracticeQuestionFilterChangeHandler;
    };
    navigationExpansion?: 'question' | 'type' | 'section';
  }) => void;
  clearNavigation: () => void;
  setDrawerOpen: (open: boolean) => void;
};

const emptyNavigationState = {
  navigationVisible: false,
  sidebarLabel: 'Questions',
  drawerLabel: 'Current type',
  currentQuestionId: '',
  currentIndex: 0,
  totalCount: 0,
  sidebarSections: [] as PracticeNavigationTreeSection[],
  drawerSections: [] as PracticeNavigationTreeSection[],
  onSelectQuestion: null as PracticeQuestionSelectHandler | null,
  onLoadMore: null as (() => void) | null,
  navigationLoadingMore: false,
  navigationFilterEnabled: false,
  navigationFilter: {
    frequencyScope: 'all' as const,
    newOnly: false,
  },
  navigationFilterShowFrequency: false,
  navigationFilterShowNew: false,
  onNavigationFilterChange: null as PracticeQuestionFilterChangeHandler | null,
  drawerOpen: false,
  navigationExpansion: 'question' as const,
};

export const usePracticeShellStore = create<PracticeShellState>((set) => ({
  ...emptyNavigationState,
  setNavigation: (payload) =>
    set({
      navigationVisible: payload.visible,
      sidebarLabel: payload.sidebarLabel,
      drawerLabel: payload.drawerLabel,
      currentQuestionId: payload.currentQuestionId,
      currentIndex: payload.currentIndex,
      totalCount: payload.totalCount,
      sidebarSections: payload.sidebarSections,
      drawerSections: payload.drawerSections,
      onSelectQuestion: payload.onSelectQuestion,
      onLoadMore: payload.onLoadMore || null,
      navigationLoadingMore: Boolean(payload.navigationLoadingMore),
      navigationFilterEnabled: Boolean(payload.navigationFilter?.enabled),
      navigationFilter: payload.navigationFilter?.value || emptyNavigationState.navigationFilter,
      navigationFilterShowFrequency: Boolean(payload.navigationFilter?.showFrequency),
      navigationFilterShowNew: Boolean(payload.navigationFilter?.showNew),
      onNavigationFilterChange: payload.navigationFilter?.onChange || null,
      navigationExpansion: payload.navigationExpansion || 'question',
    }),
  clearNavigation: () =>
    set({
      ...emptyNavigationState,
    }),
  setDrawerOpen: (open) => set({ drawerOpen: open }),
}));
